import {
  CreateResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  GetListParams,
  GetListResult,
  UpdateResult,
  UpdateParams,
  DeleteParams,
  DeleteResult,
  CreateParams,
} from "react-admin";
import { DrivingSchoolLabelDto, schemas, serverAPI } from "../api/server.api";
import { applyFilter, applyPagination, applySort, splitCompositeId } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { z } from "zod";

export const DrivingSchoolLabelColorsEnum = schemas.DrivingSchoolLabelDto.shape.color;

export interface DrivingSchoolLabel extends DrivingSchoolLabelDto {
  color: z.infer<typeof DrivingSchoolLabelColorsEnum>;
}

class DrivingSchoolLabelsProvider extends AbstractDataProvider<DrivingSchoolLabel> {
  async getOne(_: string, { id }: GetOneParams<DrivingSchoolLabel>): Promise<GetOneResult<DrivingSchoolLabel>> {
    const [drivingSchoolId, labelId] = splitCompositeId(id);
    const drivingSchoolLabel = await serverAPI.getDrivingSchoolLabel({ id: labelId, drivingSchoolId });
    return { data: _toRecord(drivingSchoolLabel) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<DrivingSchoolLabel>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const drivingSchoolLabels = await serverAPI.getDrivingSchoolLabels({ drivingSchoolId });
    return applyPagination(
      applySort(applyFilter(drivingSchoolLabels.map(_toRecord), clientSideFilter), sort),
      pagination,
    );
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<DrivingSchoolLabel>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("drivingSchoolLabels", { filter: { drivingSchoolId: id, ...filter }, sort, pagination });
  }

  async create(
    _resource: string,
    {
      data,
      meta,
    }: {
      data: CreateParams<DrivingSchoolLabel>;
      meta: Record<string, any>;
    },
  ): Promise<CreateResult<DrivingSchoolLabel>> {
    if (!meta || !meta.drivingSchoolId) {
      throw new Error(`Invalid meta: ${JSON.stringify(meta)} -- expected: "drivingSchoolId"`);
    }
    const payload = schemas.CreateDrivingSchoolLabelDto.parse(data);
    const drivingSchoolLabel = await serverAPI.createDrivingSchoolLabel({
      payload,
      drivingSchoolId: meta.drivingSchoolId,
    });
    return { data: _toRecord(drivingSchoolLabel) };
  }

  async update(_resource: string, params: UpdateParams<DrivingSchoolLabel>): Promise<UpdateResult<DrivingSchoolLabel>> {
    const { id, data } = params;
    const [drivingSchoolId, labelId] = splitCompositeId(id);
    const drivingSchoolLabel = await serverAPI.updateDrivingSchoolLabel({
      id: labelId,
      payload: data,
      drivingSchoolId,
    });
    return { data: _toRecord(drivingSchoolLabel) };
  }

  async delete(_resource: string, params: DeleteParams<DrivingSchoolLabel>): Promise<DeleteResult> {
    const { id } = params;
    const [drivingSchoolId, labelId] = splitCompositeId(id);
    await serverAPI.deleteDrivingSchoolLabel({ id: labelId, drivingSchoolId });
    return { data: {} };
  }
}

export const drivingSchoolLabelsProvider = new DrivingSchoolLabelsProvider();

function _toRecord(dto: DrivingSchoolLabelDto): DrivingSchoolLabel {
  return {
    ...dto,
    id: `${dto.drivingSchoolId}:${dto.id}`,
  };
}
